import tw from "twin.macro"
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { Filter } from "../../../clients/FiltersClient.d"
import { scrollIntoView } from "seamless-scroll-polyfill"
import { LanguageContext } from "../../../contexts/Language"
import { InventoryContext } from "../../../contexts/Inventory/context"
import { FiltersContext } from "../../../contexts/Filters/context"
import { checkAllAccessoryCodesExist } from "../../../helpers"
import FilterTitle from "./FilterTitle"
import AnimatedChevron from "../../atoms/Accordion/AnimatedChevron"

// Create a wrapper component for individual filter rendering
const DynamicFilters: React.FC<{
  filter: Filter
  index: number
  modalOpen: boolean
  filterTypes: Map<string, React.FC>
  hideYearFilter: boolean
}> = ({ filter, index, modalOpen, filterTypes, hideYearFilter }) => {
  const FilterComponent: any = filterTypes.get(filter.type)
  const content = useRef<HTMLDivElement | null>(null)
  const elementRef = useRef<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [transitionEnded, setTransitionEnded] = useState(false)
  const { _ } = useContext(LanguageContext)
  const [state, dispatch] = useContext(InventoryContext)
  const [filtersState] = useContext(FiltersContext)

  // Memoize getFilterCountFromState to reduce unnecessary recalculations
  const getFilterCountFromState = useMemo(() => {
    return (filter: Filter) => {
      let filterState = 0

      if (Array.isArray(filtersState.selectedFilters[filter.paramName])) {
        filterState = filtersState.selectedFilters[filter.paramName].length
      } else if (filtersState.selectedFilters[filter.paramName]) {
        filterState++
      }

      // Determine if custom x series custom models and modelGroups are selected.
      if (
        filter.paramName === "modelNumber" &&
        filtersState.selectedFilters?.accessoryModels
      ) {
        // X Series Custom Models
        const modelsWithAccessoryCodes = filter?.values?.filter(
          value => value?.accessoryCodes,
        )
        modelsWithAccessoryCodes?.length > 0 &&
          modelsWithAccessoryCodes.forEach(model => {
            const areInState = checkAllAccessoryCodesExist(
              model?.accessoryCodes!,
              filtersState.selectedFilters["accessoryModels"],
            )
            areInState && filterState++
          })

        // X Series Custom Model Groups
        const modelGroupsWithAccessoryCodes = filter.values
          ?.map(({ models }) => {
            const modelsWithAccessoryCodes = models?.filter(
              model => model.accessoryCodes,
            )
            return modelsWithAccessoryCodes
          })
          ?.filter(value => value?.length! > 0)
          ?.flat()
        modelGroupsWithAccessoryCodes?.length > 0 &&
          modelGroupsWithAccessoryCodes.forEach(model => {
            const areInState = checkAllAccessoryCodesExist(
              model?.accessoryCodes!,
              filtersState.selectedFilters["accessoryModels"],
            )
            areInState && filterState++
          })
      }

      // Add selected X-Series to accessory filter count
      if (filter.paramName === "accessory") {
        filter.values?.forEach(accessoryFilter => {
          if (accessoryFilter?.subCategory !== "X-Series") return
          const areInState = checkAllAccessoryCodesExist(
            accessoryFilter.value.toString(),
            filtersState.selectedFilters["accessoryModels"],
          )

          areInState && filterState++
        })
      }

      return filterState
    }
  }, [filtersState.selectedFilters])

  // Safe scroll function with null checks
  const safeScrollIntoView = useCallback(() => {
    if (!elementRef.current) return

    try {
      setTimeout(() => {
        if (
          isExpanded &&
          filter.name !== "INTERIOR COLOR" &&
          elementRef.current
        ) {
          scrollIntoView(elementRef.current, {
            behavior: "smooth",
            inline: "start",
            block: "center",
          })
        } else if (isExpanded && elementRef.current) {
          scrollIntoView(elementRef.current, {
            behavior: "smooth",
            inline: "end",
            block: "center",
          })
        }
      }, 300)
    } catch (error) {
      console.warn("Scroll into view failed:", error)
    }
  }, [isExpanded, filter.name])

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    const handleTransitionEnd = () => {
      setTimeout(() => {
        setTransitionEnded(true)
      }, 700)
    }

    element.addEventListener("transitionend", handleTransitionEnd)
    return () => {
      element.removeEventListener("transitionend", handleTransitionEnd)
    }
  }, [modalOpen])

  useEffect(() => {
    safeScrollIntoView()
  }, [safeScrollIntoView])

  useEffect(() => {
    if (modalOpen) {
      setIsExpanded(false)
    }
  }, [modalOpen])

  const toggleExpand = useCallback((e: React.MouseEvent) => {
    // Check if the click is on the button or its children
    if (
      buttonRef.current &&
      (buttonRef.current === e.target ||
        buttonRef.current.contains(e.target as Node))
    ) {
      setIsExpanded(prev => !prev)
      setTransitionEnded(prev => !prev)
    }
  }, [])

  return (
    <div
      key={index}
      ref={elementRef}
      css={[
        tw`w-full border-b border-gray-200 py-4 [&:last-of-type]:border-b-0`,
        transitionEnded ? tw`overflow-visible` : tw`overflow-hidden`,
        isExpanded && tw`transition-all ease-out duration-500 pb-6`,
      ]}
    >
      <button
        ref={buttonRef}
        type="button"
        css={[
          tw`flex justify-between items-center w-full`,
          tw`focus-visible:(outline-gray-50)`,
          "-webkit-tap-highlight-color: transparent;",
          filter.name === "YEAR" && hideYearFilter && !state.year
            ? tw`hidden`
            : tw`flex`,
        ]}
        aria-expanded={isExpanded}
        onClick={toggleExpand}
      >
        <FilterTitle
          title={filter.name}
          isExpanded={isExpanded}
          isTransitioned={transitionEnded}
          count={getFilterCountFromState(filter)}
          showCount={getFilterCountFromState(filter) > 0}
        />
        <AnimatedChevron isExpanded={isExpanded} />
      </button>

      <div
        ref={node => {
          content.current = node
          elementRef.current = node
        }}
        css={[
          tw`transition-all ease-out duration-500 opacity-100 px-4 h-0`,
          transitionEnded ? tw`overflow-visible` : tw`overflow-hidden`,
          isExpanded ? `height: auto` : tw`overflow-hidden`,
        ]}
      >
        <FilterComponent {...filter} isExpanded={isExpanded} />
      </div>
    </div>
  )
}
export default DynamicFilters
